import type { PartType, RekeySettings } from '../../hooks/useGetSettings';
import type { InsuranceSearchResult } from '../../hooks/useSearchInsurance';
import type { SubCompanyMode } from './SubCompanyTable';

export type ApuCustomEntry = RekeySettings['apuMaximization']['custom'][number];
export type ApuCarrier = ApuCustomEntry['insuranceCompanies'][number];

/** One override being added, before it is merged into the saved list. */
export interface ApuOverrideDraft {
    insurerId: string;
    insurerName: string;
    /** Group the carrier belongs to; what its sub companies are looked up by. */
    groupId?: string;
    /** What to do with each company in the group, by insurer id. */
    subModes: Record<string, SubCompanyMode>;
    /**
     * Set when the carrier folds into a group mate's existing override instead
     * of getting one of its own: no new row, and the mate's part type stands.
     */
    combineWithId?: string;
    combineWithName?: string;
    partType: PartType;
}

export const buildApuOverrideDraft = (defaultPartType: PartType): ApuOverrideDraft => ({
    insurerId: '',
    insurerName: '',
    groupId: undefined,
    subModes: {},
    combineWithId: undefined,
    combineWithName: undefined,
    partType: defaultPartType,
});

/** The override that already names this carrier, if one does. */
export const apuEntryForCarrier = (
    custom: Array<ApuCustomEntry>,
    insurerId: string,
): ApuCustomEntry | undefined =>
    custom.find((entry) => entry.insuranceCompanies.some((company) => company._id === insurerId));

/**
 * This carrier plus the sub companies riding on its DRP settings. They share
 * one override, the same way they share one set of DRP rules.
 */
export const carriersForDrpApu = (
    primary: ApuCarrier,
    relatedCompanies: Array<{ _id: string; name: string }>,
    subModes: Record<string, string>,
): Array<ApuCarrier> => [
    primary,
    ...relatedCompanies
        .filter((company) => subModes[company._id] === 'same')
        .map((company) => ({ _id: company._id, name: company.name })),
];

/**
 * Store one override for these carriers, or clear it when partType is null so
 * they fall back to the organization default.
 *
 * The list stays `{ insuranceCompanies, partType }`. Carriers this call places
 * are pulled out of whatever entry held them first; an entry left with nobody
 * on it is dropped. Everyone else is left where they are.
 */
export const setApuOverrideForCarriers = (
    custom: Array<ApuCustomEntry>,
    carriers: Array<ApuCarrier>,
    partType: PartType | null,
): Array<ApuCustomEntry> => {
    const ids = new Set(carriers.map((carrier) => carrier._id));
    const next = custom
        .map((entry) => ({
            ...entry,
            insuranceCompanies: entry.insuranceCompanies.filter((company) => !ids.has(company._id)),
        }))
        .filter((entry) => entry.insuranceCompanies.length > 0);
    if (!partType || carriers.length === 0) return next;
    return [...next, { insuranceCompanies: carriers, partType }];
};

/** Every carrier already covered by an override, by insurer id. */
export const coveredCarrierIds = (custom: Array<ApuCustomEntry>): Set<string> => {
    const ids = new Set<string>();
    for (const entry of custom) {
        for (const company of entry.insuranceCompanies) ids.add(company._id);
    }
    return ids;
};

/**
 * The override a group mate already owns, offered as something to fold into.
 * Two carriers from one group nearly always re-key the same way, so joining the
 * existing row is usually what a shop means when it adds the second name.
 */
export const apuMergeTargetFor = (
    draft: Pick<ApuOverrideDraft, 'insurerId'>,
    relatedCompanies: Array<InsuranceSearchResult>,
    custom: Array<ApuCustomEntry>,
): { insurerId: string; insurerName: string; partType: PartType } | undefined => {
    const relatedIds = new Set(relatedCompanies.map((company) => company._id));
    for (const entry of custom) {
        const mate = entry.insuranceCompanies.find(
            (company) => company._id !== draft.insurerId && relatedIds.has(company._id),
        );
        if (mate) {
            return { insurerId: mate._id, insurerName: mate.name, partType: entry.partType };
        }
    }
    return undefined;
};

/**
 * The mode each group company starts on. A company already covered by some
 * override is 'elsewhere' — this modal leaves it where it is unless the user
 * pulls it in — and everything else rides along, because a group is normally
 * taken on whole. 'separate' is never seeded: branching is a choice made here.
 */
export const seedApuSubModes = (
    relatedCompanies: Array<InsuranceSearchResult>,
    custom: Array<ApuCustomEntry>,
): Record<string, SubCompanyMode> => {
    const covered = coveredCarrierIds(custom);
    const modes: Record<string, SubCompanyMode> = {};
    for (const company of relatedCompanies) {
        modes[company._id] = covered.has(company._id) ? 'elsewhere' : 'same';
    }
    return modes;
};

export interface BuildApuCustomArgs {
    /** The saved override list this draft is merged into. */
    custom: Array<ApuCustomEntry>;
    draft: ApuOverrideDraft;
    /** Companies in the carrier's group, the carrier itself excluded. */
    relatedCompanies: Array<InsuranceSearchResult>;
}

/**
 * The whole custom override list as it should look after the modal saves.
 *
 * A carrier belongs to exactly one override, so anything this draft places is
 * first pulled out of the entry it was in — an entry left with no carriers goes
 * with it. Carriers riding along share the primary's row; branched ones get a
 * row of their own, starting from the same part type.
 */
export const buildApuCustom = ({
    custom,
    draft,
    relatedCompanies,
}: BuildApuCustomArgs): Array<ApuCustomEntry> => {
    const nameById = new Map(relatedCompanies.map((company) => [company._id, company.name]));

    const sharedIds = relatedCompanies
        .map((company) => company._id)
        .filter((id) => draft.subModes[id] === 'same');
    const branchedIds = relatedCompanies
        .map((company) => company._id)
        .filter((id) => draft.subModes[id] === 'separate');

    const placedIds = new Set([draft.insurerId, ...sharedIds, ...branchedIds]);

    // Pull every carrier this draft places out of wherever it sat, and drop the
    // entries that end up empty.
    let next = custom
        .map((entry) => ({
            ...entry,
            insuranceCompanies: entry.insuranceCompanies.filter(
                (company) => !placedIds.has(company._id),
            ),
        }))
        .filter((entry) => entry.insuranceCompanies.length > 0);

    const primaryGroup: Array<ApuCarrier> = [
        { _id: draft.insurerId, name: draft.insurerName },
        ...sharedIds.map((id) => ({ _id: id, name: nameById.get(id) ?? '' })),
    ];

    if (draft.combineWithId) {
        // Folding in writes no row of its own: the carrier joins the mate's
        // override and takes the part type already set on it.
        const targetIndex = next.findIndex((entry) =>
            entry.insuranceCompanies.some((company) => company._id === draft.combineWithId),
        );
        next = targetIndex === -1
            ? [...next, { insuranceCompanies: primaryGroup, partType: draft.partType }]
            : next.map((entry, index) =>
                index === targetIndex
                    ? {
                        ...entry,
                        insuranceCompanies: [...entry.insuranceCompanies, ...primaryGroup],
                    }
                    : entry,
            );
    } else {
        next = [...next, { insuranceCompanies: primaryGroup, partType: draft.partType }];
    }

    for (const id of branchedIds) {
        next = [
            ...next,
            {
                insuranceCompanies: [{ _id: id, name: nameById.get(id) ?? '' }],
                partType: draft.partType,
            },
        ];
    }

    return next;
};
